import { Stack } from "@mantine/core";
import { UseFormReturnType } from "@mantine/form";

import { useBillingPipelineFeature } from "@/billing/hooks/useBillingPipelineFeature";
import { EncounterAnswersFormType } from "@/encounters/zod-schemas/encounter-answers";
import { NewbornIPPEncounterFormType } from "@/encounters/zod-schemas/newborn-ipp-encounter";
import { CptCopyrightNotice } from "@/shared/cpt-copyright";

import { EncounterDiagnoses } from "../encounter-diagnoses";
import { EncounterClientSummaryAndInstructions } from "../EncounterForms/EncounterNotes";
import { EncounterTimeSpent } from "../EncounterTimeSpent";
import { LabOrdersList } from "../LabOrders";

import DxPlanDiagnosesSection from "./DxPlanDiagnosesSection";

interface DxPlanTabPanelProps {
  form: UseFormReturnType<EncounterAnswersFormType | NewbornIPPEncounterFormType>;
  isEncounterActive: boolean;
  isAmendment?: boolean;
  onProcedureDiagnosisEditClick?: () => void;
  /**
   * Makes Procedures the active tab. Only the shell that has one passes it, so the Diagnoses
   * section can leave out the controls that would have nowhere to go.
   */
  onOpenProceduresTab?: () => void;
}

export default function DxPlanTabPanel({
  form,
  isEncounterActive,
  isAmendment,
  onProcedureDiagnosisEditClick,
  onOpenProceduresTab,
}: DxPlanTabPanelProps) {
  const isBillingPipelineEnabled = useBillingPipelineFeature();

  return (
    <Stack pt="xl" gap="lg">
      <EncounterTimeSpent isEditable={isEncounterActive || Boolean(isAmendment)} />
      {/* One Diagnoses section either way: the flag swaps the section, it does not add a second. */}
      {isBillingPipelineEnabled ? (
        <EncounterDiagnoses
          isAmendment={isAmendment}
          isEncounterActive={isEncounterActive}
          onOpenProceduresTab={onOpenProceduresTab}
        />
      ) : (
        <DxPlanDiagnosesSection
          form={form}
          isAmendment={isAmendment}
          isEncounterActive={isEncounterActive}
          onProcedureDiagnosisEditClick={onProcedureDiagnosisEditClick}
        />
      )}
      <LabOrdersList isAmendment={isAmendment} isEncounterActive={isEncounterActive} />
      <EncounterClientSummaryAndInstructions
        form={form}
        isAmendment={isAmendment}
        isEncounterActive={isEncounterActive}
      />
      <CptCopyrightNotice variant="bold" />
    </Stack>
  );
}
